/**
 * PlanAssignDialog — give a reading plan to churches, campuses, a whole
 * province (everything beneath it) or an event's registered attendees.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Search, Church, Globe2, CalendarDays } from 'lucide-react';

const today = () => new Date().toISOString().slice(0, 10);

const TargetList = ({ rows, selected, toggle, testPrefix, icon: Icon, emptyNote }) => (
  <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 divide-y">
    {rows.length === 0 && <p className="p-3 text-xs text-slate-500">{emptyNote}</p>}
    {rows.map((r) => (
      <label
        key={r.id}
        className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50"
        data-testid={`${testPrefix}-${r.id}`}
      >
        <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} />
        <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className="text-sm text-slate-800 flex-1 min-w-0 truncate">{r.name}</span>
        {r.level && r.level !== 'event' && (
          <Badge variant="outline" className="text-[10px] capitalize shrink-0">
            {String(r.level).replace(/_/g, ' ')}
          </Badge>
        )}
        {r.start_date && (
          <span className="text-[10px] text-slate-400 shrink-0">{r.start_date}</span>
        )}
      </label>
    ))}
  </div>
);

export const PlanAssignDialog = ({ plan, open, onOpenChange }) => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [churches, setChurches] = useState(new Set());
  const [provinces, setProvinces] = useState(new Set());
  const [events, setEvents] = useState(new Set());
  const [startDate, setStartDate] = useState(today());
  const [active, setActive] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['bible-assignment-targets'],
    queryFn: async () => (await api.get('/admin/bible/assignment-targets')).data,
    enabled: open,
  });

  const filter = (rows) => {
    const q = search.trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) => (r.name || '').toLowerCase().includes(q));
  };

  const toggler = (set, setter) => (id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const total = churches.size + provinces.size + events.size;

  const targets = useMemo(() => [
    ...[...churches].map((id) => ({ type: 'church', id })),
    ...[...provinces].map((id) => ({ type: 'province', id })),
    ...[...events].map((id) => ({ type: 'event', id })),
  ], [churches, provinces, events]);

  const save = useMutation({
    mutationFn: async () => (await api.post('/admin/bible/plan-assignments', {
      plan_id: plan.id, targets, start_date: startDate, is_active: active,
    })).data,
    onSuccess: (res) => {
      const n = res.created + res.updated;
      toast.success(`${plan.title} assigned to ${n} target${n === 1 ? '' : 's'}`, {
        description: `Starts ${res.start_date}. Members see it as soon as they sign in.`,
      });
      qc.invalidateQueries({ queryKey: ['bible-plan-assignments'] });
      setChurches(new Set()); setProvinces(new Set()); setEvents(new Set());
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!plan) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="plan-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign “{plan.title}”</DialogTitle>
          <DialogDescription>
            Pick as many targets as you like. A province gives the plan to every church beneath
            it; an event gives it to everyone registered. It appears in their reading plans and
            starts itself on the start date.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Search churches, provinces or events"
                     className="pl-9" data-testid="assign-search" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Churches &amp; campuses</Label>
              <TargetList rows={filter(data?.churches)} selected={churches}
                          toggle={toggler(churches, setChurches)} icon={Church}
                          testPrefix="assign-church"
                          emptyNote="No congregations match that search." />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">
                Provinces, districts &amp; country offices
                <span className="ml-1.5 font-normal text-slate-500">
                  — reaches every church beneath
                </span>
              </Label>
              <TargetList rows={filter(data?.provinces)} selected={provinces}
                          toggle={toggler(provinces, setProvinces)} icon={Globe2}
                          testPrefix="assign-province"
                          emptyNote="No groupings match that search." />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Events — everyone registered</Label>
              <TargetList rows={filter(data?.events)} selected={events}
                          toggle={toggler(events, setEvents)} icon={CalendarDays}
                          testPrefix="assign-event"
                          emptyNote="No events match that search." />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div className="space-y-1.5">
                <Label className="text-xs">Start date</Label>
                <Input type="date" value={startDate}
                       onChange={(e) => setStartDate(e.target.value)}
                       data-testid="assign-start-date" />
              </div>
              <label className="flex items-end gap-2 text-sm text-slate-700 pb-2">
                <Switch checked={active} onCheckedChange={setActive}
                        data-testid="assign-active-switch" />
                Active straight away
              </label>
            </div>
          </div>
        )}

        <DialogFooter>
          <span className="mr-auto text-xs text-slate-500" data-testid="assign-count">
            {total} target{total === 1 ? '' : 's'} selected
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!total || save.isPending}
                  data-testid="assign-save">
            {save.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Assign plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlanAssignDialog;
